import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { Toaster } from "sonner";
import MetaPixelTracker from "@/components/MetaPixelTracker";
import ModalSesionVencida from "@/components/ModalSesionVencida";

import LandingPage from "@/pages/LandingPage";
import ProbarPage from "@/pages/ProbarPage";
import LoginPage from "@/pages/LoginPage";
import OtpLoginPage from "@/pages/OtpLoginPage";
import RegisterPage from "@/pages/RegisterPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import EvaluationsPage from "@/pages/EvaluationsPage";
import NewEvaluationPage from "@/pages/NewEvaluationPage";
import EvaluationDetailPage from "@/pages/EvaluationDetailPage";
import GroupsPage from "@/pages/GroupsPage";
import AlumnosPage from "@/pages/AlumnosPage";
import AlumnoFichaPage from "@/pages/AlumnoFichaPage";
import RetestPage from "@/pages/RetestPage";
import MedicionFuncionalPage from "@/pages/MedicionFuncionalPage";
import SettingsPage from "@/pages/SettingsPage";
import TermsPage from "@/pages/TermsPage";
import PrivacyPage from "@/pages/PrivacyPage";
import PublicEvaluationPage from "@/pages/PublicEvaluationPage";
import StudentIntakePage from "@/pages/StudentIntakePage";
import AdminDashboardPage from "@/pages/AdminDashboardPage";
import CheckoutPage from "@/pages/CheckoutPage";
import AdminDecisionTestsPage from "@/pages/AdminDecisionTestsPage";
import BajaMailsPage from "@/pages/BajaMailsPage";

// Error Boundary to catch rendering errors
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
          <div className="bg-white rounded-lg shadow-lg p-6 max-w-md text-center">
            <h1 className="text-xl font-bold text-red-600 mb-2">Error en la aplicación</h1>
            <p className="text-gray-600 mb-4">Ha ocurrido un error. Por favor recarga la página.</p>
            <p className="text-xs text-gray-400 mb-4">{this.state.error?.message}</p>
            <button 
              onClick={() => window.location.reload()} 
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
            >
              Recargar página
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1F3A5F]" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1F3A5F]" /></div>;
  if (user) return <Navigate to="/alumnos" replace />;
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public - no auth */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/checkout" element={<CheckoutPage />} />
      <Route path="/evaluate" element={<PublicEvaluationPage />} />
      {/* Destino del anuncio de intensidad: 3 preguntas con el motor real */}
      <Route path="/probar" element={<ProbarPage />} />
      <Route path="/entrevista/:token" element={<StudentIntakePage />} />

      {/* Payment result pages (MercadoPago back_urls) */}

      {/* Auth pages */}
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/auth/otp" element={<PublicRoute><OtpLoginPage /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/terminos" element={<TermsPage />} />
      <Route path="/privacidad" element={<PrivacyPage />} />
      <Route path="/baja" element={<BajaMailsPage />} />

      {/* Protected - full users */}
      {/* El panel principal se retiro: Alumnos es la pantalla de inicio. Se deja
          la redireccion para no romper enlaces guardados ni el historial. */}
      <Route path="/dashboard" element={<Navigate to="/alumnos" replace />} />
      <Route path="/admin/dashboard" element={<ProtectedRoute><AdminDashboardPage /></ProtectedRoute>} />
      <Route path="/admin/decision-tests" element={<ProtectedRoute><AdminDecisionTestsPage /></ProtectedRoute>} />
      <Route path="/evaluations" element={<ProtectedRoute><EvaluationsPage /></ProtectedRoute>} />
      <Route path="/alumnos" element={<ProtectedRoute><AlumnosPage /></ProtectedRoute>} />
      <Route path="/alumnos/:id" element={<ProtectedRoute><AlumnoFichaPage /></ProtectedRoute>} />
      <Route path="/alumnos/:id/retest" element={<ProtectedRoute><RetestPage /></ProtectedRoute>} />
      <Route path="/alumnos/:id/pruebas" element={<ProtectedRoute><MedicionFuncionalPage /></ProtectedRoute>} />
      <Route path="/groups" element={<ProtectedRoute><GroupsPage /></ProtectedRoute>} />
      <Route path="/configuracion" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      <Route path="/new-evaluation" element={<ProtectedRoute><NewEvaluationPage /></ProtectedRoute>} />
      <Route path="/evaluation/:id" element={<ProtectedRoute><EvaluationDetailPage /></ProtectedRoute>} />

      {/* Legacy redirects */}
      <Route path="/try-evaluation" element={<Navigate to="/evaluate" replace />} />
      {/* Ads-friendly deep link: lleva a la sección "Prueba gratuita" en la landing */}
      <Route path="/prueba" element={<Navigate to="/#prueba" replace />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

// En el sitio de prueba (REACT_APP_ENTORNO=prueba en Netlify) una etiqueta fija
// lo avisa, para no confundirlo nunca con producción. En producción la
// variable no está y no se muestra nada.
function FranjaEntorno() {
  if (process.env.REACT_APP_ENTORNO !== 'prueba') return null;
  return (
    <div
      // Abajo a la izquierda: arriba la app tiene barras fijas que taparía.
      className="fixed bottom-3 left-3 z-[100] rounded-full bg-amber-400 px-3 py-1 text-xs font-bold tracking-wide text-amber-950 shadow-lg pointer-events-none"
      data-testid="franja-entorno-prueba"
    >
      ENTORNO DE PRUEBA
    </div>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <FranjaEntorno />
          <ModalSesionVencida />
          <MetaPixelTracker />
          <AppRoutes />
          <Toaster position="top-right" richColors closeButton toastOptions={{ className: "font-sans" }} />
        </BrowserRouter>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
